/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import type { EditorAgent } from '../../../utils/agents';
import { useAgentRefinementTagProps } from '../../hooks/use-agent-refinement-tag-props';
import { LatestPromptRenderer } from '../LatestPromptRenderer/LatestPromptRenderer';
import { RefinementTag } from '../RefinementTag/RefinementTag';

import { messages } from './messages';

const PROMPT_CONTENT_ID = 'ai-latest-prompt-content';

const headerEditStyles = css({
	cursor: 'pointer',
	border: 'none',
	width: '100%',
	backgroundColor: 'transparent',
	color: 'inherit',
	textAlign: 'start',
	fontSize: 'unset',
	lineHeight: 'inherit',
	borderRadius: token('border.radius.200', '4px'),
	borderBottomLeftRadius: 0,
	borderBottomRightRadius: 0,
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered', '#091E420F'),
		textDecoration: 'underline',
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed', '#091E4224'),
	},
	'&:focus': {
		outline: `2px solid ${token('color.border.focused', '#4C9AFF')}`,
		outlineOffset: token('space.negative.025', '-2px'),
	},
});

const headerStyles = css({
	padding: token('space.150', '12px'),
	overflowWrap: 'break-word',
});

const headerWrapperStyles = css({
	display: 'flex',
});

const headerEditRendererStyles = css({
	'&:hover': {
		textDecoration: 'none',
	},
});

type Props = {
	prompt?: string;
	promptADF?: string;
	presetTitle?: string;
	onEditPromptClick?: () => void;
	providerFactory: ProviderFactory;
	schema: Schema;
	agent?: EditorAgent;
};

export const LatestPromptHeader = ({
	prompt,
	promptADF,
	presetTitle,
	providerFactory,
	onEditPromptClick,
	agent,
}: Props) => {
	const { formatMessage } = useIntl();

	const promptContent = React.useMemo(() => {
		// Use renderer only if FF is on and we have user input in ADF.
		if (promptADF && fg('platform.editor.ai-lightweight-editor-prompt_f92x1')) {
			return <LatestPromptRenderer promptADF={promptADF} providerFactory={providerFactory} />;
		}

		return <>{prompt}</>;
	}, [prompt, promptADF, providerFactory]);

	const refinementTagProps = useAgentRefinementTagProps(agent);
	const promptRendererEnabled =
		promptADF && fg('platform.editor.ai-lightweight-editor-prompt_f92x1');

	const header = React.useMemo(() => {
		return (
			<>
				{presetTitle && <RefinementTag label={presetTitle} {...refinementTagProps} />}
				{promptContent}
			</>
		);
	}, [presetTitle, refinementTagProps, promptContent]);

	if (!(prompt || presetTitle)) {
		return null;
	}

	return (
		<div data-testid="prompt-header">
			{!onEditPromptClick ? (
				<div
					css={[headerStyles, promptRendererEnabled && headerWrapperStyles]}
					id={PROMPT_CONTENT_ID}
				>
					{header}
				</div>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button
				<button
					type="button"
					data-testid="edit-prompt-button"
					onClick={onEditPromptClick}
					css={[headerStyles, headerEditStyles, promptRendererEnabled && headerEditRendererStyles]}
					aria-label={formatMessage(messages.editPrompt)}
					aria-describedby={PROMPT_CONTENT_ID}
				>
					<div css={[promptRendererEnabled && headerWrapperStyles]} id={PROMPT_CONTENT_ID}>
						{header}
					</div>
				</button>
			)}
		</div>
	);
};
